import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { PortMacEntry } from '../api/types'
import { t } from '../i18n/lingua'
import { DataTable } from './DataTable'

/**
 * La finestra di una porta di uno switch: **che cosa vede**.
 *
 * Una riga per indirizzo imparato sulla porta (tabella MAC dell'ultima
 * scansione SNMP, più le voci non più viste, segnate): il MAC, l'IP se in
 * CMDB c'è, il costruttore dal registro IEEE o dal catalogo di CENSO, il CI
 * abbinato con modello, la VLAN, l'ultima volta. Un costruttore di
 * elettromedicali si evidenzia — è un giudizio sul costruttore, non sul
 * dispositivo, e la riga lo dice.
 *
 * Da qui si **segnala** un MAC all'amministrazione di CENSO: chi sta davanti
 * all'armadio sa che cosa c'è attaccato («il monitor della sala 3») e con una
 * riga lo insegna alla piattaforma; quando la segnalazione è catalogata, il
 * riconoscimento vale per tutti i clienti.
 */
export function FinestraPortaMac({
  ciId,
  porta,
  voci,
  fingerbank,
  onChiudi,
  onAggiorna,
}: {
  ciId: string
  porta: string
  voci: PortMacEntry[]
  /** C'è una credenziale Fingerbank: si offre «Riconosci». */
  fingerbank?: boolean
  onChiudi: () => void
  /** Dopo un riconoscimento: la lista si rilegge. */
  onAggiorna?: () => void
}) {
  const [riconoscendo, setRiconoscendo] = useState(false)
  const [segnalando, setSegnalando] = useState<PortMacEntry | null>(null)
  const [nota, setNota] = useState('')
  const [esito, setEsito] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const correnti = voci.filter((v) => v.current).length

  async function riconosci() {
    setRiconoscendo(true)
    setEsito(null)
    try {
      const r = await api.enrichPortMacs(ciId, porta)
      setEsito(
        r.error
          ? t('Fingerbank: {n} chiesti, {r} riconosciuti, poi: {e}', { n: r.asked, r: r.recognized, e: r.error })
          : t('Fingerbank: {n} chiesti, {r} riconosciuti, {c} CI classificati. Le risposte sono nel catalogo CENSO.', { n: r.asked, r: r.recognized, c: r.classified }),
      )
      onAggiorna?.()
    } catch (e) {
      setEsito(e instanceof Error ? e.message : String(e))
    } finally {
      setRiconoscendo(false)
    }
  }

  async function segnala() {
    if (!segnalando) return
    setBusy(true)
    setEsito(null)
    try {
      await api.reportPortMac(ciId, { mac: segnalando.mac, if_name: porta, note: nota })
      setEsito(t('Segnalazione inviata: quando l’amministrazione di CENSO la cataloga, il riconoscimento vale per tutti.'))
      setSegnalando(null)
      setNota('')
    } catch (e) {
      setEsito(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Porta {p}', { p: porta })}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{t('Porta {p}: cosa vede', { p: porta })}</h3>
            <p className="dob-modal__sotto">
              {t('{n} indirizzi all’ultima scansione, {m} in tutto (le voci non più viste restano per novanta giorni).', {
                n: correnti, m: voci.length,
              })}
            </p>
          </div>
          <div className="dob-modal__comandi">
            {fingerbank && (
              <button type="button" className="secondary" onClick={() => void riconosci()} disabled={riconoscendo}
                      title={t('Chiede a Fingerbank gli indirizzi che il catalogo CENSO non conosce (al massimo cento) e salva le risposte con la classe.')}>
                {riconoscendo ? t('Riconosco…') : t('Riconosci con Fingerbank')}
              </button>
            )}
            <button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
          </div>
        </div>
        {esito && <p className="field__hint">{esito}</p>}
        <div className="dob-modal__corpo">
          <DataTable
            rows={voci}
            rowKey={(v) => v.id}
            rowClassName={(v) => (v.current ? undefined : 'riga-sbiadita')}
            conteggio={{ nome: t('indirizzi') }}
            emptyText={t('Nessun indirizzo.')}
            columns={[
              {
                key: 'mac', header: 'MAC', mono: true,
                value: (v) => (v.current ? v.mac : `${v.mac} ${t('non più vista')}`),
                render: (v) => (
                  <>
                    {v.mac}
                    {!v.current && (
                      <>
                        {' '}<span className="badge" title={t('Non vista all’ultima scansione')}>{t('non più vista')}</span>
                      </>
                    )}
                  </>
                ),
              },
              { key: 'ip', header: 'IP', mono: true },
              {
                key: 'name', header: t('Nome'),
                value: (v) => [v.hostname ?? v.ntopng_name, v.wire_kind, v.wire_facts, v.ntopng_name && v.hostname ? v.ntopng_name : null, v.ntopng_os, ...(v.seen_sources ?? [])].filter(Boolean).join(' '),
                render: (v) => (
                  <>
                    {v.hostname ?? v.ntopng_name ?? '—'}
                    {v.wire_facts && (
                      <>
                        <br />
                        <small className="text-muted" title={t('Dai protocolli sul filo (mirror/promiscuo)')}>{t('filo')}: {v.wire_kind ? `${v.wire_kind} · ` : ''}{v.wire_facts}</small>
                      </>
                    )}
                    {(v.ntopng_os || (v.ntopng_name && v.hostname)) && (
                      <>
                        <br />
                        <small className="text-muted" title={t('Visto da ntopng')}>ntopng: {[v.ntopng_name && v.hostname ? v.ntopng_name : null, v.ntopng_os].filter(Boolean).join(' · ')}</small>
                      </>
                    )}
                    {(v.seen_sources?.length ?? 0) > 0 && (
                      <>
                        <br />
                        <small className="text-muted" title={t('Fonti dell’ascolto passivo')}>{v.seen_sources!.join(', ')}</small>
                      </>
                    )}
                  </>
                ),
              },
              {
                key: 'vendor', header: t('Costruttore'),
                value: (v) => (v.masked ? t('elettromedicale') : [
                  v.locally_administered ? t('MAC casuale') : (v.vendor ?? t('sconosciuto')),
                  v.platform,
                  v.medical ? `${t('elettromedicale')} ${v.medical}` : null,
                ].filter(Boolean).join(' ')),
                render: (v) => v.masked ? (
                  <span className="badge badge--warn" title={t('I dettagli degli elettromedicali sono riservati all’Ingegneria clinica e agli amministratori (CENSO MED).')}>
                    {t('elettromedicale')}
                  </span>
                ) : (
                  <>
                    {v.locally_administered ? (
                      <span className="badge" title={t('MAC randomizzato dal dispositivo (privacy)')}>{t('MAC casuale')}</span>
                    ) : (
                      v.vendor ?? <span className="text-muted">{t('sconosciuto')}</span>
                    )}
                    {v.platform && <> · <span className="text-muted">{v.platform}</span></>}
                    {v.medical && (
                      <>
                        {' '}
                        <span className="badge badge--warn" title={t('Il costruttore produce apparecchi elettromedicali: è un indizio sul costruttore, non sul dispositivo.')}>
                          {t('elettromedicale')} · {v.medical}
                        </span>
                      </>
                    )}
                  </>
                ),
              },
              {
                key: 'object', header: t('Oggetto'),
                value: (v) => (v.masked ? [t('Dispositivo medico collegato')] : v.ci_id
                  ? [v.ci_name ?? v.ci_id, v.ci_vendor, v.ci_model]
                  : v.known_model
                    ? [v.known_model, v.known_category, v.known_class, t('catalogo CENSO')]
                    : v.seen_kind || v.seen_model
                      ? [v.seen_model, v.seen_kind, t('annunciato')]
                      : [t('non in CMDB')]
                ).filter(Boolean).join(' '),
                render: (v) => (
                  v.masked ? (
                    <span className="text-muted">{t('Dispositivo medico collegato')}</span>
                  ) : v.ci_id ? (
                    <>
                      <Link to={`/cis/${v.ci_id}`}>{v.ci_name ?? v.ci_id}</Link>
                      {(v.ci_vendor || v.ci_model) && (
                        <span className="text-muted"> · {[v.ci_vendor, v.ci_model].filter(Boolean).join(' ')}</span>
                      )}
                    </>
                  ) : v.known_model ? (
                    <span title={v.known_note ?? undefined}>
                      {v.known_model}
                      {v.known_category && <span className="text-muted"> · {v.known_category}</span>}
                      {v.known_class && <span className="text-muted"> · {v.known_class}</span>}
                      {' '}<span className="badge badge--ok">{t('catalogo CENSO')}</span>
                    </span>
                  ) : v.seen_kind || v.seen_model ? (
                    <span title={t('Riconosciuto da ciò che il dispositivo annuncia in rete (mDNS, DHCP, LLDP, SSDP).')}>
                      {v.seen_model ?? ''}
                      {v.seen_model && v.seen_kind ? ' · ' : ''}
                      {v.seen_kind ?? ''}
                      {' '}<span className="badge">{t('annunciato')}</span>
                    </span>
                  ) : (
                    <span className="text-muted">{t('non in CMDB')}</span>
                  )
                ),
              },
              { key: 'vlan', header: 'VLAN', mono: true },
              {
                key: 'last_seen', header: t('Ultima vista'), type: 'date', mono: true,
                render: (v) => (v.last_seen ? v.last_seen.slice(0, 16).replace('T', ' ') : '—'),
              },
              {
                key: 'actions', header: '', sort: false, filter: false, searchable: false,
                render: (v) => (
                  <span className="rowactions">
                    <button type="button" className="link" onClick={() => { setSegnalando(v); setNota('') }}>
                      {t('Segnala')}
                    </button>
                  </span>
                ),
              },
            ]}
          />
        </div>
        {segnalando && (
          <div className="dob-modal__piede" style={{ marginTop: 12 }}>
            <p style={{ margin: '0 0 6px' }}>
              <strong>{t('Segnala {mac} all’amministrazione di CENSO', { mac: segnalando.mac })}</strong>
              {segnalando.vendor && <span className="text-muted"> · {segnalando.vendor}</span>}
            </p>
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('Scrivi che cos’è, se lo sai (marca, modello, reparto): chi cataloga non vede la tua rete. Una volta catalogato, CENSO lo riconoscerà ovunque.')}
            </p>
            <textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              rows={2}
              style={{ width: '100%' }}
              placeholder={t('es. pompa di infusione B. Braun Infusomat, sala 2')}
            />
            <div className="rowactions" style={{ textAlign: 'left', marginTop: 8 }}>
              <button type="button" onClick={() => void segnala()} disabled={busy}>
                {busy ? t('Invio…') : t('Invia la segnalazione')}
              </button>
              <button type="button" className="secondary" onClick={() => setSegnalando(null)}>{t('Annulla')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
